import React, {useState} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import {
  addToast,
  Button,
  Dialogue,
  Modal,
  Icon,
  Text,
  Anchor,
  DropdownItem,
  showGlobalModal
} from '@albert-io/atomic';
import {SchoolModelV5} from 'resources/GeneratedModels/School/SchoolModel.v5';

import {genericMandarkRequest, invalidatePartialInterest} from 'resources/mandark.resource';
import {pluralize} from 'lib/stringUtils';

import './notify-teachers.scss';

/**
 * Invalidates queries related to the sending of a notifcation.
 *
 * @param {string} schoolId The school.id of the school we just sent a notification for.
 */
function invalidateRelatedQueries(schoolId) {
  /**
   * This is a bit sweeping at the moment just based on how these components use
   * the request found in the school administrator dashboard.
   */
  invalidatePartialInterest({
    resourcePath: ['schools_v5', schoolId]
  });
}

export const NotifyTeachersModal = ({handleClose, school}) => {
  const [isRequestInFlight, setInFlight] = useState(false);

  async function sendLicenseNotification(schoolToNotify) {
    setInFlight(true);
    let toast;
    try {
      await genericMandarkRequest('patch', {
        resourcePath: ['schools_v5', schoolToNotify.getId(), 'notify_personnel_of_license']
      });
      toast = {
        title: 'Emails sent!',
        type: 'success'
      };
    } catch (e) {
      toast = {
        message: 'An error was encountered while attempting to send your notification.',
        title: 'Something went wrong',
        type: 'danger'
      };
    } finally {
      setInFlight(false);
      addToast(toast);
      invalidateRelatedQueries(schoolToNotify.getId());
    }
  }

  return (
    <Modal ariaLabel='Send License Notification' handleClose={handleClose}>
      {({CloseButtonWrapper, modalContentStyle}) => {
        return (
          <Dialogue
            inModal
            hideCloseBtn
            className={`notify-teachers-dialogue ${modalContentStyle}`}
            handleClose={handleClose}
          >
            <Dialogue.Title align='center'>
              <Icon icon={['fal', 'envelope']} className='u-color_slate-400' />
            </Dialogue.Title>
            <Dialogue.Body align='center'>
              An email will be sent to all approved teachers at {school.getName()} who have not
              enrolled students in any active classrooms.
            </Dialogue.Body>
            <Dialogue.BtnGroup className='notify-teachers-dialogue__btn-group u-flex-direction_column'>
              <Button
                onClick={() => sendLicenseNotification(school)}
                disabled={isRequestInFlight}
                className='u-width_100pc'
              >
                Send email(s)
              </Button>
              <CloseButtonWrapper>
                <Button className='u-width_100pc' color='secondary'>
                  Cancel
                </Button>
              </CloseButtonWrapper>
            </Dialogue.BtnGroup>
          </Dialogue>
        );
      }}
    </Modal>
  );
};

NotifyTeachersModal.propTypes = {
  handleClose: PropTypes.func,
  school: PropTypes.instanceOf(SchoolModelV5)
};

function getResendHoursTimeout(lastNotificationSent) {
  if (!lastNotificationSent) {
    return 0;
  }
  return 24 - moment().diff(lastNotificationSent, 'hours');
}

/**
 * Returns messaging around the last time a notification was sent.
 *
 * @param {null|import('moment')} lastNotificationSent The time of the last license notification.
 * @returns {React.node} Copy to rendered alongside the notify teachers message.
 * @see https://github.com/albert-io/project-management/issues/4644
 */
function getLastNotificationCopy(lastNotificationSent) {
  if (!lastNotificationSent) {
    return null;
  }
  const sentMoreThanSevenDaysAgo = lastNotificationSent.isBefore(moment().subtract(7, 'days'));
  const displayFormat = sentMoreThanSevenDaysAgo ? 'MM/DD/YYYY' : 'dddd';
  return (
    <p>This email was last sent to teachers on {lastNotificationSent.format(displayFormat)}.</p>
  );
}

/**
 * This message is only displayed based on the school's license and notification state.
 *
 * @see https://github.com/albert-io/project-management/issues/4245
 * @returns {React.Node}
 */
export const NotifyTeachersMessage = ({school}) => {
  const [visible, setVisible] = useState(false);
  const countOfApprovedTeachers = school.getMeta().getCountOfApprovedTeachers();
  const canResendInHours = getResendHoursTimeout(school.getLicenseNotificationSentAt());
  if (
    school.getMeta().isLicenseActive() === false ||
    countOfApprovedTeachers === 0 ||
    canResendInHours > 0
  ) {
    return null;
  }

  return (
    <>
      <Text emphasis='medium' size='s'>
        Would you like to{' '}
        <Anchor onClick={() => setVisible(true)}>
          notify your approved {pluralize('teacher', countOfApprovedTeachers)}
        </Anchor>{' '}
        of your active license?
        <Text size='xs' emphasis='lowest'>
          {getLastNotificationCopy(school.getLicenseNotificationSentAt())}
        </Text>
      </Text>
      {visible && <NotifyTeachersModal school={school} handleClose={() => setVisible(false)} />}
    </>
  );
};

NotifyTeachersMessage.propTypes = {
  school: PropTypes.instanceOf(SchoolModelV5)
};

export const NotifyTeachersDropdownItem = ({school}) => {
  const [visible, setVisible] = useState(false);
  /**
   * The subtext of the DropdownItem vary based on license, personnel and notification state.
   *
   * @see https://github.com/albert-io/project-management/issues/4245
   */
  let subtext = null;
  let disabled = false;

  const canResendInHours = getResendHoursTimeout(school.getLicenseNotificationSentAt());

  if (school.getMeta().isLicenseActive() === false) {
    subtext = 'Your school does not have an active license.';
    disabled = true;
  } else if (school.getMeta().getCountOfApprovedTeachers() === 0) {
    subtext = 'You do not have any approved teachers to notify.';
    disabled = true;
  } else if (canResendInHours > 0) {
    const lastNotifiedDay = moment().isSame(school.getLicenseNotificationSentAt(), 'day')
      ? 'today'
      : school.getLicenseNotificationSentAt().format('dddd');
    subtext = `Last notified ${lastNotifiedDay}. Available to resend in ${canResendInHours} ${pluralize(
      'hour',
      canResendInHours
    )}.`;
    disabled = true;
  }
  /**
   * This is a bit of a unique use of `<DropdownItem>` where the disabled state
   * also contains informative `subtext`. We override the default emphasis for
   * `subtext` when disabled to make sure it has proper contrast.
   */
  const subtextProps = disabled
    ? {
        emphasis: 'medium'
      }
    : {};

  /**
   * Since the `<NotifyTeachersDropdownItem>` is displayed within a `<Popper>` the
   * modal must be displayed as a "global modal" otherwise events will be swallowed.
   */
  if (visible) {
    showGlobalModal(({handleClose}) => (
      <NotifyTeachersModal school={school} handleClose={handleClose} />
    ));
  }

  return (
    <DropdownItem
      data-testid='notify-teachers-dropdown-item'
      subtext={subtext}
      disabled={disabled}
      subtextProps={subtextProps}
      onClick={() => setVisible(true)}
    >
      Notify teachers of active license.
    </DropdownItem>
  );
};

NotifyTeachersDropdownItem.propTypes = {
  school: PropTypes.instanceOf(SchoolModelV5)
};
